// Global timeline feed shown in the default side panel (Figma "Global timeline"): today's events,
// the still-open dated tasks and the waiting-on emails, each with its due status.
//
// The BFF (`GET v1/timeline/global`) sends each row's due INSTANT and no status — its payload is
// cached, so a status baked in there would freeze. `now` lives in this store and is ticked by the
// panel, which is what makes an item due in 61 minutes turn into "Due soon" on its own.
//
// Row shape (what the panel renders):
//   { id, type: 'event'|'task'|'note'|'message'|'research', title, typeLabel, date, time,
//     schedule, checklistCount, dueAmount, status, statusKey, statusLabel, statusColor,
//     statusText, statusTokenColor, statusTooltip, route }
//
// `title` is the item's OWN text and leads the row; `typeLabel` is the KIND-line under it
// ("Event created" / "Task created", or the bare noun for a row nobody created); `date`/`time` are
// the CREATION stamp on the right and `schedule` is when the thing itself happens ("Today, 8pm") —
// the arrangement Figma 40006440:182480 draws.
//
// `dueAmount` ("2h ago" / "in 45m") is computed against the same `now`, so how late a row is stays
// live on the tick as well.

import api from '@/lib/services/general.service'
import { buildGlobalTimelineRows } from '@/lib/globalTimelineFeed'

// How often the clock ticks. A minute is enough: the only boundary that matters is the 1-hour
// "due soon" line, and being at most a minute late crossing it is invisible.
export const TICK_MS = 60 * 1000

// How often the feed itself is re-read, so a task added elsewhere in the app joins today's list
// without a reload. The endpoint's own cache is 1 minute, so this always sees fresh data.
export const REFRESH_MS = 5 * 60 * 1000

export const state = {
  // Raw payload items, exactly as the endpoint sent them.
  raw: [],
  now: Date.now(),
  isLoading: false,
  loaded: false,
}

export const getters = {
  // The rows the panel draws: the five nearest DUE or OVERDUE items, newest deadline first
  // (issue #208 — the rule itself is in @/lib/globalTimelineFeed). Recomputes whenever `now` ticks,
  // so an upcoming item joins the list the moment it comes due, and the fifth row it displaces
  // leaves on the same tick.
  items: s => buildGlobalTimelineRows(s.raw, s.now),
  isLoading: s => s.isLoading,
  loaded: s => s.loaded,
  now: s => s.now,
}

// The interval handles are module state, not store state — Vuex shouldn't hold timers.
let timer = null
let refreshTimer = null

// The read in flight, if any. ONE request is ever out for this feed (busy-my-web#438): on a browser
// refresh the panel asks on mount and the boot sequence asks as soon as the session is known, and
// the two used to be two identical reads of an endpoint that composes several services — the
// slowest thing on the home page, paid for twice. Module state rather than store state for the same
// reason the timers are: a promise is not something Vuex should hold.
let inFlight = null

// The read itself. Kept apart from the action so the action carries only the two rules around it:
// signed-in callers only, and one request at a time.
async function readFeed(commit) {
  commit('SET_LOADING', true)
  try {
    const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
    const response = await api.get('timeline/global', { timeZoneId })
    const items = response?.data?.items
    commit('SET_ITEMS', Array.isArray(items) ? items : [])
  } catch {
    commit('SET_ITEMS', [])
  } finally {
    commit('SET_NOW', Date.now())
    commit('SET_LOADING', false)
    commit('SET_LOADED', true)
  }
}

export const actions = {
  // Loads today's feed for the caller's zone ("today" and the due window are computed there).
  async fetch({ commit, rootGetters }) {
    // Signed-in only (see followUps/fetch): a guest's panel stays empty rather than bouncing the
    // request off the identity provider on mount and on every refresh tick (2026-08-29).
    if (!rootGetters['user/isAuthenticated']) return
    // Already asking — every caller waits on that one answer rather than starting a second.
    if (inFlight) return inFlight
    inFlight = readFeed(commit)
    try {
      await inFlight
    } finally {
      inFlight = null
    }
  },

  // Keeps the statuses live without a reload. Idempotent — mounting the panel twice starts one clock.
  startClock({ commit, dispatch }) {
    if (timer) return
    timer = setInterval(() => commit('SET_NOW', Date.now()), TICK_MS)
    refreshTimer = setInterval(() => dispatch('fetch'), REFRESH_MS)
  },

  stopClock() {
    if (timer) clearInterval(timer)
    if (refreshTimer) clearInterval(refreshTimer)
    timer = null
    refreshTimer = null
  },
}

export const mutations = {
  SET_ITEMS(s, val) { s.raw = val },
  // Drop every row that belonged to one of the given timelines — what deleting an ongoing orphans
  // (busy-my-web#55). The panel is rebuilt from `raw` by a getter, so removing the payload items is
  // enough for the rows to disappear on the same tick, before the reconciling fetch comes back.
  // An empty/absent list is a no-op: an older BFF that names no timelines must not blank the panel.
  REMOVE_BY_TIMELINE_IDS(s, ids) {
    const gone = new Set((Array.isArray(ids) ? ids : []).filter(Boolean).map(id => String(id).toLowerCase()))
    if (!gone.size) return
    s.raw = (s.raw || []).filter(i => !(i?.timelineId && gone.has(String(i.timelineId).toLowerCase())))
  },
  SET_NOW(s, val) { s.now = val },
  SET_LOADING(s, val) { s.isLoading = val },
  SET_LOADED(s, val) { s.loaded = val },
}
